import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Folder,
  File as FileIcon,
  Grid,
  List,
  Plus,
  MoreVertical,
  ChevronRight,
  Search,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Video,
  FileCode,
  Loader2,
  Settings,
  FolderTree,
  X,
  Filter,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { DndProvider, useDrag, useDragLayer, useDrop } from "react-dnd";
import { getEmptyImage, HTML5Backend } from "react-dnd-html5-backend";
import api from "../../api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "../../css/RepoOrganizer.css";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";
import changeStatusIcon from "../../assets/icons/changeStatusIcon.png";

const formatBytes = (bytes, decimals = 1) => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const ITEM_TYPE = "REPO_ITEM";

const canModifyRepoItem = (item, role) =>
  role === "admin" ||
  (item.ownerType !== "admin" &&
    item.mode !== "global" &&
    item.mode !== "group");

const RepoDragPreview = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) {
    return null;
  }

  const dragCount = item.count || 1;
  const isFolderDrag = item.items?.every((dragItem) => dragItem.type === "folder");

  return (
    <div
      className="repo-drag-preview-layer"
      style={{
        transform: `translate(${currentOffset.x + 14}px, ${currentOffset.y + 14}px)`,
      }}
    >
      <div className="repo-drag-preview-stack" />
      <div className="repo-drag-preview-card">
        {isFolderDrag ? (
          <Folder size={16} className="repo-folder-icon" />
        ) : (
          <FileIcon size={16} className="repo-file-icon" />
        )}
        <span className="repo-drag-preview-name" title={item.name}>
          {item.name}
        </span>
      </div>
      {dragCount > 1 && (
        <span className="repo-drag-preview-badge">{dragCount}</span>
      )}
    </div>
  );
};

// --- Draggable/Droppable Item Component ---
const DraggableItem = ({
  item,
  viewMode,
  onNavigate,
  onToggleSelect,
  onAction,
  isSelected,
  getFileIcon,
  onMove,
  role,
  onApprovalStatusChange,
  dragSelectionRef,
  selectedDragItems,
}) => {
  // Check if user can move/modify this item
  // In the refined system, admins can modify everything. Regular users can modify personal items only.
  const canModify = canModifyRepoItem(item, role);
  const dragItems =
    isSelected && selectedDragItems.length > 0 ? selectedDragItems : [item];

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ITEM_TYPE,
    item: () => ({
      id: item.id,
      type: item.type,
      name: item.name,
      items: dragItems,
      count: dragItems.length,
    }),
    canDrag: canModify,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [item, dragItems, canModify]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const getDraggedItems = (draggedItem) => draggedItem.items || [draggedItem];

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    canDrop: (draggedItem) => {
      const draggedItems = getDraggedItems(draggedItem);
      const draggedIds = new Set(draggedItems.map((dragItem) => dragItem.id));
      return item.type === "folder" && !draggedIds.has(item.id) && canModify;
    },
    drop: (draggedItem) => onMove(getDraggedItems(draggedItem), item.id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const combinedRef = (node) => {
    drag(node);
    if (item.type === "folder") drop(node);
  };

  if (viewMode === "grid") {
    return (
      <div
        ref={combinedRef}
        data-item-id={String(item.id)}
        className={`repo-card ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onClick={(e) => {
          if (dragSelectionRef.current) {
            return;
          }
          if (item.type === "file") {
            onToggleSelect(item.id);
          }
        }}
        onDoubleClick={(e) => {
          if (item.type === "folder") {
            onNavigate(item.id, item.name);
          }
        }}
      >
        <div
          className="repo-card-checkbox"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(item.id);
          }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            style={{ cursor: "pointer" }}
          />
        </div>

        <div className="repo-card-icon">
          {item.type === "folder" ? (
            <Folder
              className="repo-folder-icon"
              size={48}
              fill="currentColor"
              fillOpacity={0.2}
            />
          ) : (
            getFileIcon(item.name)
          )}
        </div>

        <div className="repo-card-content">
          <div className="repo-card-name" title={item.name}>
            {item.name}
          </div>
          <div className="repo-card-details">
            <span className="repo-card-type">
              {item.type === "folder"
                ? "Folder"
                : item.name.split(".").pop().toUpperCase()}
            </span>
            {item.type === "file" && item.size && (
              <span className="repo-card-size">• {formatBytes(item.size)}</span>
            )}
          </div>
          <div className="repo-card-date">
            {new Date(item.updatedAt || item.createdAt).toLocaleDateString()}
          </div>
        </div>

        <div className="repo-card-footer">
          {item.type === "file" && (
            <span
              className={`badge ${item.fileStatus === "1" ? "bg-success" : "bg-secondary"}`}
              style={{ fontSize: "0.6rem" }}
            >
              {item.fileStatus === "1" ? "Active" : "Inactive"}
            </span>
          )}
          {item.mode === "global" && (
            <span className="badge bg-info" style={{ fontSize: "0.6rem" }}>
              Global
            </span>
          )}
        </div>

        <div className="repo-card-actions">
          <div className="dropdown border-0">
            <button
              className="repo-action-dot bg-transparent border-0"
              data-bs-toggle="dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={14} />
            </button>
            <ul className="dropdown-menu">
              {item.type === "file" && (
                <li>
                  <button
                    className="dropdown-item "
                    style={{ maxWidth: "100%" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("download", item);
                    }}
                  >
                    <Download size={14} /> Download
                  </button>
                </li>
              )}
              {item.type === "file" &&
                (item.ownerType === "admin" || role === "admin") && (
                  <li>
                    <button
                      className="dropdown-item "
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction("status", item);
                      }}
                    >
                      <Settings size={14} className="icon-image" /> Change
                      Status
                    </button>
                  </li>
                )}
              {canModify && (
                <>
                  <li>
                    <button
                      className="dropdown-item "
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction("move", item);
                      }}
                    >
                      <FolderTree size={14} /> Move
                    </button>
                  </li>
                  {/* <li>
                    <hr className="dropdown-divider" />
                  </li> */}
                  {/* <li>
                    <button
                      className="dropdown-item text-danger"
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction("delete", item);
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </li> */}
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr
      ref={combinedRef}
      data-item-id={String(item.id)}
      className={`repo-list-row ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <td style={{ width: "40px" }} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(item.id)}
          style={{ cursor: "pointer" }}
        />
      </td>
      <td
        onClick={(e) => {
          if (dragSelectionRef.current) {
            return;
          }
          if (item.type === "file") {
            onToggleSelect(item.id);
          }
        }}
        onDoubleClick={(e) => {
          if (item.type === "folder") {
            onNavigate(item.id, item.name);
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {item.type === "folder" ? (
            <Folder size={18} className="repo-folder-icon" />
          ) : (
            <FileIcon size={18} className="repo-file-icon" />
          )}
          <span
            style={{
              maxWidth: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name}
          </span>
          {/* {item.mode === "global" && (
            <span className="badge bg-info ms-2" style={{ fontSize: "0.6rem" }}>
              Global
            </span>
          )} */}
        </div>
      </td>
      <td>
        {item.type === "folder"
          ? "Folder"
          : item.name.split(".").pop().toUpperCase()}
      </td>
      <td>
        {item.type === "file"
          ? item.size
            ? formatBytes(item.size)
            : "0 Bytes"
          : "--"}
      </td>
      <td>
        {item.type === "file" &&
          (role === "admin" ? (
            <select
              className={`badge ${
                item.approvalStatus === "approved"
                  ? "bg-success"
                  : item.approvalStatus === "duplicate"
                    ? "bg-secondary"
                    : item.approvalStatus === "pending"
                      ? "bg-warning"
                      : "bg-danger"
              }`}
              value={item.approvalStatus || "pending"}
              onChange={(e) => {
                e.stopPropagation();
                onApprovalStatusChange &&
                  onApprovalStatusChange(item.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "pointer", border: "none", outline: "none" }}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="duplicate">Duplicate</option>
            </select>
          ) : (
            <span
              className={`badge ${
                item.approvalStatus === "approved"
                  ? "bg-success"
                  : item.approvalStatus === "duplicate"
                    ? "bg-secondary"
                    : item.approvalStatus === "pending"
                      ? "bg-warning"
                      : "bg-danger"
              }`}
            >
              {(item.approvalStatus || "Pending").charAt(0).toUpperCase() +
                (item.approvalStatus || "Pending").slice(1)}
            </span>
          ))}
      </td>
      <td>
        {item.type === "file" && (
          <span
            className={`badge ${item.fileStatus === "1" ? "bg-success" : "bg-secondary"}`}
          >
            {item.fileStatus === "1" ? "Active" : "Inactive"}
          </span>
        )}
      </td>
      <td>
        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}
      </td>
      <td>
        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "N/A"}
      </td>
      {role === "admin" && <td>{item.uploadedBy || "N/A"}</td>}
      <td>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === "file" && (
            <Download
              size={16}
              onClick={() => onAction("download", item)}
              style={{ cursor: "pointer" }}
            />
          )}
          {item.type === "file" && canModify &&
            (item.ownerType === "admin" || role === "admin") && (
              <img
                src={changeStatusIcon}
                className="icon-image"
                style={{
                  width: "16px",
                  height: "16px",
                  cursor:
                    item.mode?.toLowerCase() === "global" ||
                    item.approvalStatus === "duplicate"
                      ? "not-allowed"
                      : "pointer",
                }}
                onClick={() => {
                  if (
                    !(
                      item.mode?.toLowerCase() === "global" ||
                      item.approvalStatus === "duplicate"
                    )
                  ) {
                    onAction("status", item);
                  }
                }}
                title="Change Status"
              />
            )}
          {canModify && (
            <FolderTree
              size={16}
              className="icon-image"
              onClick={() => onAction("move", item)}
              style={{ cursor: "pointer" }}
            />
          )}
          {/* {canModify && (
            <Trash2
              size={16}
              className="text-danger"
              onClick={() => onAction("delete", item)}
              style={{ cursor: "pointer" }}
            />
          )} */}
        </div>
      </td>
    </tr>
  );
};


const RepoOrganizer = ({ role = "user" }) => {
  // --- State ---
  const [viewMode, setViewMode] = useState("list");
  const [items, setItems] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Root" }]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [repoView, setRepoView] = useState(
    role === "admin" ? "global" : "personal",
  ); // Default based on role

  // Modal states
  const [deleteModal, setDeleteModal] = useState({ show: false, items: [] });
  const [moveModal, setMoveModal] = useState({
    show: false,
    items: [],
    targetFolderId: null,
  });
  const [createFolderModal, setCreateFolderModal] = useState({
    show: false,
    name: "",
  });
  const [dayFilter, setDayFilter] = useState("All");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [tempSortOrder, setTempSortOrder] = useState("Descending");
  const [tempDayFilter, setTempDayFilter] = useState("All");
  const [tempFolderSort, setTempFolderSort] = useState("Top");
  const [appliedSortOrder, setAppliedSortOrder] = useState("Descending");
  const [appliedFolderSort, setAppliedFolderSort] = useState("Top");

  const filterDropdownRef = useRef(null);
  const containerRef = useRef(null);
  const selectionStartRef = useRef({ x: 0, y: 0 });
  const selectionActiveRef = useRef(false);
  const dragSelectionRef = useRef(false);
  const [selectionRect, setSelectionRect] = useState(null);
  const fetchAbortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const isInteractiveSelectionTarget = (target) => {
    return !!target.closest(
      "button, input, select, textarea, label, .repo-action-dot, .dropdown-menu, .repo-card-actions, .repo-search-container, .repo-modal, .repo-modal-overlay, .repo-filter-dropdown-wrapper",
    );
  };

  const selectItemsInRect = useCallback((rect) => {
    if (!containerRef.current || !rect) return;
    const selected = new Set();
    containerRef.current
      .querySelectorAll("[data-item-id]")
      .forEach((node) => {
        const itemRect = node.getBoundingClientRect();
        if (
          itemRect.right >= rect.left &&
          itemRect.left <= rect.right &&
          itemRect.bottom >= rect.top &&
          itemRect.top <= rect.bottom
        ) {
          selected.add(node.dataset.itemId);
        }
      });
    setSelectedItems(selected);
  }, []);

  const updateSelection = useCallback(
    (clientX, clientY) => {
      if (!selectionActiveRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const startX = selectionStartRef.current.x;
      const startY = selectionStartRef.current.y;
      const left = Math.min(startX, clientX);
      const top = Math.min(startY, clientY);
      const width = Math.abs(clientX - startX);
      const height = Math.abs(clientY - startY);
      const rect = {
        left: left - containerRect.left,
        top: top - containerRect.top,
        width,
        height,
        right: left + width,
        bottom: top + height,
      };
      setSelectionRect(rect);
      selectItemsInRect(rect);
    },
    [selectItemsInRect],
  );

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    if (!containerRef.current || !containerRef.current.contains(event.target)) return;
    if (isInteractiveSelectionTarget(event.target)) return;

    const targetCard = event.target.closest(".repo-card, .repo-list-row");
    if (targetCard) {
      return;
    }

    selectionActiveRef.current = true;
    dragSelectionRef.current = false;
    selectionStartRef.current = { x: event.clientX, y: event.clientY };
    const containerRect = containerRef.current.getBoundingClientRect();
    setSelectionRect({
      left: event.clientX - containerRect.left,
      top: event.clientY - containerRect.top,
      width: 0,
      height: 0,
      right: event.clientX,
      bottom: event.clientY,
    });
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      if (
        Math.abs(event.clientX - selectionStartRef.current.x) > 5 ||
        Math.abs(event.clientY - selectionStartRef.current.y) > 5
      ) {
        dragSelectionRef.current = true;
      }
    };

    const handleMouseUp = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      selectionActiveRef.current = false;
      setSelectionRect(null);
      window.requestAnimationFrame(() => {
        dragSelectionRef.current = false;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [updateSelection]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  const handleApplyFilter = () => {
    setDayFilter(tempDayFilter);
    setAppliedSortOrder(tempSortOrder);
    setAppliedFolderSort(tempFolderSort);
    setShowFilterDropdown(false);
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setTempSortOrder("Descending");
    setTempDayFilter("All");
    setTempFolderSort("Top");
    setDayFilter("All");
    setAppliedSortOrder("Descending");
    setAppliedFolderSort("Top");
    setShowFilterDropdown(false);
    setCurrentPage(1);
  };

  // --- Derived Data ---
  const filteredItems = useMemo(() => {
    let viewFiltered = items.filter((item) => {
      if (role === "admin") return true; // Admins see everything returned by the backend
      if (repoView === "global") {
        return (
          item.ownerType === "admin" ||
          item.mode === "global" ||
          item.mode === "group"
        );
      } else {
        return (
          item.ownerType !== "admin" &&
          item.mode !== "global" &&
          item.mode !== "group"
        );
      }
    });

    let result = viewFiltered.filter((item) => {
      // Folder filter
      if (item.parentId !== currentFolder) return false;

      // Search filter
      if (
        searchTerm &&
        !item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;

      // Day Filter
      if (dayFilter && dayFilter !== "All") {
        const itemDate = new Date(item.updatedAt || item.createdAt);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dayFilter === "Today") {
          if (itemDate < today) return false;
        } else if (dayFilter === "Yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          if (itemDate < yesterday || itemDate >= today) return false;
        } else if (dayFilter === "Last 7 Days") {
          const weekAgo = new Date(today);
          weekAgo.setDate(today.getDate() - 7);
          if (itemDate < weekAgo) return false;
        }
      }

      return true;
    });

    // Apply manual Time Sort
    result.sort((a, b) => {
      if (appliedFolderSort === "Top") {
        if (a.type === "folder" && b.type !== "folder") return -1;
        if (a.type !== "folder" && b.type === "folder") return 1;
      }
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      if (appliedSortOrder === "Ascending") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

    return result;
  }, [
    items,
    currentFolder,
    searchTerm,
    repoView,
    role,
    dayFilter,
    appliedSortOrder,
    appliedFolderSort,
  ]);

  const { sortedData, requestSort, sortConfig } = useTableSort(filteredItems, {
    foldersFirst: appliedFolderSort === "Top",
  });

  const paginatedData = useMemo(() => {
    if (viewMode === "grid") return sortedData;
    const startIndex = (currentPage - 1) * entriesPerPage;
    return sortedData.slice(startIndex, startIndex + entriesPerPage);
  }, [sortedData, currentPage, entriesPerPage, viewMode]);

  const totalPages = Math.ceil(sortedData.length / entriesPerPage);

  // --- Fetch Data ---
  const fetchItems = useCallback(async () => {
    // Cancel previous request if still in progress
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;
    setIsLoading(true);

    try {
      // Fetch both endpoints in parallel for better performance
      const [trainedRes, docsRes] = await Promise.all([
        api.get("/trained-documents/get-trained-docs", {
          signal: controller.signal,
        }),
        role === "user" || role === "admin"
          ? api.get("/uploaded-files/global-user-docs", {
              signal: controller.signal,
            })
          : Promise.resolve({ data: { data: [] } }),
      ]);

      // If this request was aborted during the fetch, stop processing
      if (controller.signal.aborted) return;

      // Build organized files set for quick lookup
      const organizedFilesSet = new Set(
        trainedRes.data
          .filter((item) => item.itemType === "file" && item.uploaded_file_id)
          .map((f) => String(f.uploaded_file_id)),
      );

      // Get all docs and filter out already organized ones
      const allUserDocs = docsRes.data?.data || docsRes.data || [];
      const unorganizedFiles = allUserDocs.filter(
        (doc) =>
          !organizedFilesSet.has(String(doc.id || doc.file_id)) &&
          !organizedFilesSet.has(String(doc.trained_document_id)),
      );

      // Transform trained items (folders + organized files)
      const trainedItems = trainedRes.data.map((item) => ({
        id: String(item.id),
        name: item.name,
        type: item.itemType === "folder" ? "folder" : "file",
        parentId: item.parentId ? String(item.parentId) : null,
        size: item.size,
        updatedAt: item.updatedAt,
        uploadedBy: item.uploadedBy,
        uploaded_file_id: item.uploaded_file_id,
        pythonFileUuid: item.pythonFileUuid,
        mode: item.mode,
        ownerType: item.ownerType || "user",
        isUnorganized: false,
        approvalStatus: item.status || "approved",
        fileStatus: item.fileStatus || "1",
        createdAt: item.createdAt || item.updatedAt,
      }));

      // Transform unorganized files
      const unorganizedItems = unorganizedFiles.map((doc) => ({
        id: `unorganized-${doc.id || doc.file_id}`,
        name: doc.name || doc.original_filename,
        type: "file",
        parentId: doc.folder_id ? String(doc.folder_id) : null,
        size: doc.size || doc.file_size,
        updatedAt: doc.updatedAt || doc.created_timestamp,
        uploadedBy: doc.uploaded_by || "Me",
        uploaded_file_id: doc.id || doc.file_id,
        pythonFileUuid: doc.python_file_uuid,
        mode: doc.mode || doc.upload_mode,
        ownerType:
          doc.mode === "global" ||
          doc.mode === "group" ||
          doc.upload_mode === "global" ||
          doc.upload_mode === "group"
            ? "admin"
            : "user",
        isUnorganized: true,
        approvalStatus: doc.approval_status || "approved",
        fileStatus: doc.file_status || "1",
        createdAt: doc.createdAt,
      }));

      // Combine and deduplicate by using a Map
      const itemsMap = new Map();
      trainedItems.forEach((item) => itemsMap.set(item.id, item));
      unorganizedItems.forEach((item) => {
        if (!itemsMap.has(item.id)) itemsMap.set(item.id, item);
      });

      setItems(Array.from(itemsMap.values()));
    } catch (err) {
      if (
        err.name === "AbortError" ||
        err.name === "CanceledError" ||
        err.code === "ERR_CANCELED" ||
        controller.signal.aborted
      ) {
        console.log("Fetch cancelled");
        return;
      }
      console.error("Fetch items error:", err);
      showErrorToast("Failed to load repository items");
    } finally {
      // Only set loading to false if this request is still the active one
      if (fetchAbortControllerRef.current === controller) {
        setIsLoading(false);
      }
    }
  }, [role]);

  useEffect(() => {
    fetchItems();
    return () => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [fetchItems]);

  // --- Handlers ---
  const handleNavigate = (folderId, folderName) => {
    const safeFolderId = folderId ? String(folderId) : null;
    setCurrentFolder(safeFolderId);
    setSelectedItems(new Set());
    if (safeFolderId === null) {
      setBreadcrumbs([{ id: null, name: "Root" }]);
    } else {
      setBreadcrumbs((prev) => {
        const index = prev.findIndex((b) => b.id === safeFolderId);
        if (index !== -1) return prev.slice(0, index + 1);
        return [...prev, { id: safeFolderId, name: folderName }];
      });
    }
    setSearchTerm("");
  };

  const replaceFolderIdInState = (tempId, realId) => {
    const nextId = String(realId);

    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        id: item.id === tempId ? nextId : item.id,
        parentId: item.parentId === tempId ? nextId : item.parentId,
      })),
    );
    setCurrentFolder((prev) => (prev === tempId ? nextId : prev));
    setBreadcrumbs((prev) =>
      prev.map((crumb) => ({
        ...crumb,
        id: crumb.id === tempId ? nextId : crumb.id,
      })),
    );
    setMoveModal((prev) => ({
      ...prev,
      targetFolderId: prev.targetFolderId === tempId ? nextId : prev.targetFolderId,
      items: prev.items.map((item) => ({
        ...item,
        id: item.id === tempId ? nextId : item.id,
        parentId: item.parentId === tempId ? nextId : item.parentId,
      })),
    }));
    setDeleteModal((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({
        ...item,
        id: item.id === tempId ? nextId : item.id,
        parentId: item.parentId === tempId ? nextId : item.parentId,
      })),
    }));
    setSelectedItems((prev) => {
      if (!prev.has(tempId)) return prev;
      const next = new Set(prev);
      next.delete(tempId);
      next.add(nextId);
      return next;
    });
  };

  const handleCreateFolder = async () => {
    if (!createFolderModal.name || !createFolderModal.name.trim()) return;
    try {
      const newFolderId = `temp-${Date.now()}`;
      const optimisticFolder = {
        id: newFolderId,
        name: createFolderModal.name.trim(),
        type: "folder",
        parentId: currentFolder,
        size: 0,
        updatedAt: new Date().toISOString(),
        uploadedBy: "Me",
        uploaded_file_id: null,
        pythonFileUuid: null,
        mode: "personal",
        ownerType: "user",
        isUnorganized: false,
        approvalStatus: "approved",
        fileStatus: "1",
        createdAt: new Date().toISOString(),
      };

      // Optimistically add folder to state
      setItems((prev) => [...prev, optimisticFolder]);
      setCreateFolderModal({ show: false, name: "" });
      showSuccessToast("Folder created");

      // Make API call
      const res = await api.post("/trained-documents/create-folder", {
        name: createFolderModal.name.trim(),
        parent_id: currentFolder,
      });

      // Replace temp ID with real ID
      const createdFolderId = res.data?.data?.id;
      if (createdFolderId) {
        replaceFolderIdInState(newFolderId, createdFolderId);
      }
    } catch (err) {
      showErrorToast("Failed to create folder");
      // Revert optimistic update on error
      fetchItems();
    }
  };

  const handleDelete = async (strategy = "DELETE") => {
    const itemsToDelete = deleteModal.items;
    const organizedIds = itemsToDelete
      .filter((i) => !i.id.toString().startsWith("unorganized-"))
      .map((i) => i.id);
    const unorganizedIds = itemsToDelete
      .filter((i) => i.id.toString().startsWith("unorganized-"))
      .map((i) => i.id.replace("unorganized-", ""));

    try {
      // Optimistically remove items from state
      const deletedIds = new Set(itemsToDelete.map((i) => i.id));
      setItems((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      setDeleteModal({ show: false, items: [] });
      setSelectedItems(new Set());

      showSuccessToast(
        itemsToDelete.length > 1
          ? `Deleted ${itemsToDelete.length} items`
          : "Item deleted",
      );

      // Execute delete operations
      await Promise.all([
        organizedIds.length > 0
          ? api.delete(`/trained-documents/${organizedIds[0]}`, {
              data: { item_ids: organizedIds, delete_strategy: strategy },
            })
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) =>
          api.delete(`/uploaded-files/docs/${fileId}`),
        ),
      ]);
    } catch (err) {
      console.error("Delete error:", err);
      showErrorToast("Delete failed");
      // Refetch on error to restore state
      fetchItems();
    }
  };

  const handleDownload = async (item) => {
    if (item.type === "folder") return;
    try {
      const idToUse = item.uploaded_file_id || item.id;
      const res = await api.get(`/trained-documents/download/${idToUse}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showErrorToast("Download failed");
    }
  };

  const handleBulkDelete = async (selectedIds) => {
    const itemsToDelete = items.filter((i) => selectedIds.has(i.id));
    const organizedIds = itemsToDelete
      .filter((i) => !i.isUnorganized)
      .map((i) => i.id);
    const unorganizedIds = itemsToDelete
      .filter((i) => i.isUnorganized)
      .map((i) => i.id.replace("unorganized-", ""));

    try {
      // Optimistically remove items from state
      setItems((prev) =>
        prev.filter((item) => !selectedIds.has(item.id)),
      );
      setSelectedItems(new Set());

      showSuccessToast(`Deleted ${itemsToDelete.length} items`);

      // Execute delete operations in parallel
      await Promise.all([
        organizedIds.length > 0
          ? api.delete(`/trained-documents/${organizedIds[0]}`, {
              data: { item_ids: organizedIds, delete_strategy: "DELETE" },
            })
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) =>
          api.delete(`/uploaded-files/docs/${fileId}`),
        ),
      ]);
    } catch (err) {
      console.error("Bulk delete error:", err);
      showErrorToast("Bulk delete failed");
      // Refetch on error to restore state
      fetchItems();
    }
  };

  const handleMove = async (itemsToMove, targetFolderId) => {
    const unorganizedIds = itemsToMove
      .filter((i) => i.isUnorganized)
      .map((i) => i.id.replace("unorganized-", ""));
    const organizedItems = itemsToMove
      .filter((i) => !i.isUnorganized)
      .map((i) => ({ type: i.type, id: i.id }));

    try {
      // Optimistically update items' parentId in state
      const movedItemIds = new Set(itemsToMove.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          movedItemIds.has(item.id)
            ? { ...item, parentId: targetFolderId }
            : item,
        ),
      );

      setSelectedItems(new Set());
      showSuccessToast(`Moved ${itemsToMove.length} items`);

      // Execute both move operations in parallel
      await Promise.all([
        unorganizedIds.length > 0
          ? api.put("/trained-documents/move-uploaded-files", {
              uploaded_file_ids: unorganizedIds,
              target_folder_id: targetFolderId,
            })
          : Promise.resolve(),
        organizedItems.length > 0
          ? api.put("/trained-documents/move-items", {
              items: organizedItems,
              target_folder_id: targetFolderId,
            })
          : Promise.resolve(),
      ]);
    } catch (err) {
      console.error("Move error:", err);
      showErrorToast(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Move failed",
      );
      // Refetch on error to restore state
      fetchItems();
    }
  };

  const toggleSelect = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChangeFileStatus = async (item) => {
    if (!item.pythonFileUuid) {
      showErrorToast("File identifier missing");
      return;
    }
    try {
      // Optimistically toggle file status
      const newStatus = item.fileStatus === "1" ? "0" : "1";
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, fileStatus: newStatus } : i,
        ),
      );

      await api.patch(
        `/uploaded-files/update_file_status/${item.pythonFileUuid}`,
      );
      showSuccessToast("Status updated successfully");
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
      // Refetch on error
      fetchItems();
    }
  };

  const getApprovalFileId = (item) => {
    if (item.uploaded_file_id) return item.uploaded_file_id;
    if (typeof item.id === "string" && item.id.startsWith("unorganized-")) {
      return item.id.replace("unorganized-", "");
    }
    return item.id;
  };

  const handleChangeApprovalStatus = async (itemId, newStatus) => {
    const approvalFileId = getApprovalFileId(items.find((item) => item.id === itemId) || { id: itemId });
    
    try {
      // Optimistically update state
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, approvalStatus: newStatus } : item,
        ),
      );

      await api.post(`/uploaded-files/approve-or-reject`, {
        file_id: approvalFileId,
        approval_status: newStatus,
        notes: "",
      });
      
      showSuccessToast(`Doc status set to ${newStatus}`);
    } catch (err) {
      console.error("Error updating approval status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
      // Refetch on error
      fetchItems();
    }
  };

  const handleBulkApprovalStatus = async (newStatus) => {
    const fileItems = items.filter(
      (i) => selectedItems.has(i.id) && i.type === "file",
    );
    if (fileItems.length === 0) {
      showErrorToast("No files selected");
      return;
    }
    try {
      // Optimistically update state
      const updateIds = new Set(fileItems.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          updateIds.has(item.id)
            ? { ...item, approvalStatus: newStatus }
            : item,
        ),
      );
      setSelectedItems(new Set());

      showSuccessToast(`${fileItems.length} file(s) set to ${newStatus}`);

      // Execute all approval updates in parallel
      await Promise.all(
        fileItems.map((f) =>
          api.post(`/uploaded-files/approve-or-reject`, {
            file_id: getApprovalFileId(f),
            approval_status: newStatus,
            notes: "",
          }),
        ),
      );
    } catch (err) {
      console.error("Bulk approval error:", err);
      showErrorToast("Failed to update one or more files");
      // Refetch on error
      fetchItems();
    }
  };

  // --- Render Helpers ---
  const getFileIcon = (name) => {
    const ext = name.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "svg"].includes(ext))
      return <ImageIcon className="repo-file-icon" />;
    if (["mp4", "mov", "avi"].includes(ext))
      return <Video className="repo-file-icon" />;
    if (["pdf", "doc", "docx", "txt"].includes(ext))
      return <FileText className="repo-file-icon" />;
    if (["js", "jsx", "html", "css", "json"].includes(ext))
      return <FileCode className="repo-file-icon" />;
    return <FileIcon className="repo-file-icon" />;
  };

  const handleBulkDownload = async (selectedIds) => {
    const selectedList = items.filter((i) => selectedIds.has(i.id));
    const foldersSelected = selectedList.filter((i) => i.type === "folder");
    const filesToDownload = selectedList.filter((i) => i.type === "file");

    if (foldersSelected.length > 0 && filesToDownload.length > 0) {
      showErrorToast(
        "Folders cannot be downloaded. Only files will be processed.",
      );
    } else if (foldersSelected.length > 0) {
      showErrorToast("Cannot download folders.");
      return;
    }

    if (filesToDownload.length === 0) {
      showErrorToast("No files selected for download");
      return;
    }

    showSuccessToast(`Starting download of ${filesToDownload.length} files...`);

    for (const file of filesToDownload) {
      await handleDownload(file);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const selectedItemDetails = items.filter((i) => selectedItems.has(i.id));
  const selectedDraggableItems = selectedItemDetails.filter((item) =>
    canModifyRepoItem(item, role),
  );
  const hasSelectedFolder = selectedItemDetails.some((i) => i.type === "folder");
  const selectedFileCount = selectedItemDetails.filter((i) => i.type === "file").length;
  const canModifySelectedItems = selectedItemDetails.some(
    (item) => canModifyRepoItem(item, role),
  );

  const handleAction = (action, item) => {
    const canModify = item.ownerType !== "admin" || role === "admin";
    switch (action) {
      case "download":
        handleDownload(item);
        break;
      case "status":
        handleChangeFileStatus(item);
        break;
      case "delete":
        setDeleteModal({ show: true, items: [item] });
        break;
      case "move":
        setMoveModal({ show: true, items: [item], targetFolderId: null });
        break;
      default:
        break;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <RepoDragPreview />
      <div
        ref={containerRef}
        className="repo-container"
        onMouseDown={handleMouseDown}
      >
        <div className="repo-glass-panel repo-animate-fade">
          <div className="repo-header">
            <div className="repo-breadcrumbs">
              {breadcrumbs.map((b, i) => (
                <React.Fragment key={b.id || "root"}>
                  <span
                    className="repo-breadcrumb-item"
                    onClick={() => handleNavigate(b.id, b.name)}
                  >
                    {i === 0 ? <Folder size={18} /> : null}
                    {b.name}
                  </span>
                  {i < breadcrumbs.length - 1 && (
                    <ChevronRight
                      size={14}
                      className="repo-breadcrumb-separator"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="repo-actions">
              {!(repoView === "global" && role !== "admin") && (
                <button
                  className="repo-btn repo-btn-primary"
                  onClick={() => setCreateFolderModal({ show: true, name: "" })}
                  title="New Folder"
                >
                  <Plus size={18} />
                </button>
              )}

              <div className="repo-view-toggle">
                <button
                  className={`repo-view-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => {
                    setViewMode("grid");
                    setSelectedItems(new Set());
                  }}
                >
                  <Grid size={18} />
                </button>
                <button
                  className={`repo-view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => {
                    setViewMode("list");
                    setSelectedItems(new Set());
                  }}
                >
                  <List size={18} />
                </button>
              </div>

              {role !== "admin" && (
                <button
                  className={`repo-btn ${repoView === "global" ? "repo-btn-primary" : ""}`}
                  style={{ marginRight: "10px" }}
                  onClick={() => {
                    setRepoView(
                      repoView === "personal" ? "global" : "personal",
                    );
                    setCurrentFolder(null);
                    setBreadcrumbs([{ id: null, name: "Root" }]);
                    setSelectedItems(new Set());
                  }}
                >
                  {repoView === "personal"
                    ? "Go to Global Repository"
                    : "Go to Personal Repository"}
                </button>
              )}
            </div>
          </div>
          <div className="repo-filters-bar">
            <div className="repo-filters-left">
              <div className="repo-entries-select">
                <select
                  className="repo-select"
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>Entries per page</span>
              </div>
            </div>
            <div className="repo-filters-right">
              <div
                className="repo-search-container"
                style={{ position: "relative" }}
              >
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--repo-text-muted)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search Documents"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="repo-search-input"
                />
              </div>

              <div
                className="repo-filter-dropdown-wrapper"
                ref={filterDropdownRef}
              >
                <button
                  className={`repo-filter-toggle-btn ${showFilterDropdown ? "active" : ""}`}
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <Filter size={18} />
                </button>

                {showFilterDropdown && (
                  <div className="repo-filter-dropdown-menu">
                    <div className="repo-filter-section">
                      <div className="repo-filter-label">Time</div>
                      <div className="repo-radio-group">
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="time"
                            value="Ascending"
                            checked={tempSortOrder === "Ascending"}
                            onChange={() => setTempSortOrder("Ascending")}
                          />
                          <span className="repo-radio-custom"></span>
                          Ascending
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="time"
                            value="Descending"
                            checked={tempSortOrder === "Descending"}
                            onChange={() => setTempSortOrder("Descending")}
                          />
                          <span className="repo-radio-custom"></span>
                          Descending
                        </label>
                      </div>
                    </div>

                    <div className="repo-filter-divider"></div>

                    <div className="repo-filter-section">
                      <div className="repo-filter-label">Folders</div>
                      <div className="repo-radio-group">
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="folderSort"
                            value="Top"
                            checked={tempFolderSort === "Top"}
                            onChange={() => setTempFolderSort("Top")}
                          />
                          <span className="repo-radio-custom"></span>
                          Folders on Top
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="folderSort"
                            value="Mixed"
                            checked={tempFolderSort === "Mixed"}
                            onChange={() => setTempFolderSort("Mixed")}
                          />
                          <span className="repo-radio-custom"></span>
                          Mixed with Files
                        </label>
                      </div>
                    </div>

                    <div className="repo-filter-divider"></div>

                    <div className="repo-filter-section">
                      <div className="repo-filter-label">By Day</div>
                      <div className="repo-radio-group">
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="day"
                            value="Today"
                            checked={tempDayFilter === "Today"}
                            onChange={() => setTempDayFilter("Today")}
                          />
                          <span className="repo-radio-custom"></span>
                          Today
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="day"
                            value="Yesterday"
                            checked={tempDayFilter === "Yesterday"}
                            onChange={() => setTempDayFilter("Yesterday")}
                          />
                          <span className="repo-radio-custom"></span>
                          Yesterday
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="day"
                            value="All"
                            checked={tempDayFilter === "All"}
                            onChange={() => setTempDayFilter("All")}
                          />
                          <span className="repo-radio-custom"></span>
                          All
                        </label>
                      </div>
                    </div>

                    <div className="repo-filter-actions">
                      <button
                        className="repo-btn repo-btn-secondary"
                        onClick={handleResetFilter}
                      >
                        Reset
                      </button>
                      <button
                        className="repo-btn repo-btn-primary"
                        onClick={handleApplyFilter}
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="repo-loader-container">
              <Loader2 className="animate-spin" size={32} />
              <p className="mt-2 ">Loading repository...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div
              className="repo-dropzone"
              onDragOver={(e) => e.preventDefault()}
            >
              <Folder
                size={48}
                style={{ marginBottom: "1rem", opacity: 0.3 }}
              />
              <p>No items found in this directory</p>
              <p style={{ fontSize: "0.8rem", opacity: 0.6 }}>
                Try creating a folder or check other directories
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="repo-grid">
              {filteredItems.map((item) => (
                <DraggableItem
                  key={item.id}
                  item={item}
                  viewMode={viewMode}
                  onNavigate={handleNavigate}
                  onToggleSelect={toggleSelect}
                  onAction={handleAction}
                  isSelected={selectedItems.has(item.id)}
                  getFileIcon={getFileIcon}
                    onMove={handleMove}
                    role={role}
                    dragSelectionRef={dragSelectionRef}
                    selectedDragItems={selectedDraggableItems}
                    onApprovalStatusChange={
                      role === "admin" ? handleChangeApprovalStatus : undefined
                    }
                  />
                ))}
            </div>
          ) : (
            <table className="repo-list">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(
                            new Set(filteredItems.map((i) => i.id)),
                          );
                        } else {
                          setSelectedItems(new Set());
                        }
                      }}
                      checked={
                        filteredItems.length > 0 &&
                        selectedItems.size === filteredItems.length
                      }
                    />
                  </th>
                  <SortableHeader
                    column="name"
                    label="Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "30%" }}
                  />
                  <SortableHeader
                    column="type"
                    label="Type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "80px" }}
                  />
                  <SortableHeader
                    column="size"
                    label="Size"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "110px" }}
                  />
                  <SortableHeader
                    column="approvalStatus"
                    label="Doc Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "140px" }}
                  />
                  <SortableHeader
                    column="fileStatus"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "100px" }}
                  />
                  <SortableHeader
                    column="createdAt"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "120px" }}
                  />
                  <SortableHeader
                    column="updatedAt"
                    label="Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "120px" }}
                  />
                  {role === "admin" && (
                    <SortableHeader
                      column="uploadedBy"
                      label="Uploaded By"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      style={{ width: "150px" }}
                    />
                  )}
                  <th style={{ width: "110px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((item) => (
                  <DraggableItem
                    key={item.id}
                    item={item}
                    viewMode={viewMode}
                    onNavigate={handleNavigate}
                    onToggleSelect={toggleSelect}
                    onAction={handleAction}
                    isSelected={selectedItems.has(item.id)}
                    getFileIcon={getFileIcon}
                    onMove={handleMove}
                    role={role}
                    dragSelectionRef={dragSelectionRef}
                    selectedDragItems={selectedDraggableItems}
                    onApprovalStatusChange={
                      role === "admin" ? handleChangeApprovalStatus : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
          {viewMode === "list" && sortedData.length > entriesPerPage && (
            <div className="repo-pagination">
              <button
                className="repo-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </button>

              <div className="repo-page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (num) => (
                    <button
                      key={num}
                      className={`repo-page-num ${currentPage === num ? "active" : ""}`}
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  ),
                )}
              </div>

              <button
                className="repo-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Bar */}
      {selectedItems.size > 0 && (
        <div className="repo-floating-actions">
          <div className="repo-floating-left">
            <button
              className="repo-floating-close"
              onClick={() => setSelectedItems(new Set())}
              title="Clear selection"
            >
              <X size={18} />
            </button>
            <span className="repo-floating-count">
              {selectedItems.size} selected
            </span>
          </div>
          <div className="repo-floating-right">
            {!hasSelectedFolder && (
              <button
                className="repo-floating-btn"
                onClick={() => handleBulkDownload(selectedItems)}
                title="Download"
              >
                <Download size={20} />
              </button>
            )}
            {canModifySelectedItems && (
              <button
                className="repo-floating-btn"
                onClick={() =>
                  setMoveModal({
                    show: true,
                    items: items.filter((i) => selectedItems.has(i.id)),
                    targetFolderId: null,
                  })
                }
                title="Move"
              >
                <FolderTree size={20} />
              </button>
            )}
            {/* {canModifySelectedItems && (
              <button
                className="repo-floating-btn"
                onClick={() =>
                  setDeleteModal({
                    show: true,
                    items: items.filter((i) => selectedItems.has(i.id)),
                  })
                }
                title="Delete"
                style={{ color: "var(--danger-color)" }}
              >
                <Trash2 size={20} />
              </button>
            )} */}

            {role === "admin" && !hasSelectedFolder && selectedFileCount > 0 && (
              <>
                <div
                  style={{
                    width: "1px",
                    height: "24px",
                    background: "rgba(255,255,255,0.2)",
                    margin: "0 4px",
                  }}
                />
                <button
                  className="repo-floating-btn"
                  onClick={() => handleBulkApprovalStatus("approved")}
                  title="Approve"
                  style={{ color: "#4ade80" }}
                >
                  <CheckCircle size={20} />
                </button>
                <button
                  className="repo-floating-btn"
                  onClick={() => handleBulkApprovalStatus("pending")}
                  title="Set Pending"
                  style={{ color: "#fbbf24" }}
                >
                  <Clock size={20} />
                </button>
                <button
                  className="repo-floating-btn"
                  onClick={() => handleBulkApprovalStatus("rejected")}
                  title="Reject"
                  style={{ color: "#f87171" }}
                >
                  <XCircle size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div
          className="repo-modal-overlay"
          onClick={() => setDeleteModal({ show: false, items: [] })}
        >
          <div
            className="repo-modal repo-animate-fade"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="repo-modal-close"
              onClick={() => setDeleteModal({ show: false, items: [] })}
            >
              <X size={20} />
            </button>

            {deleteModal.items.length === 1 &&
            deleteModal.items[0].type === "folder" ? (
              <>
                <h3>Delete Folder</h3>
                <p>
                  What would you like to do with the files inside{" "}
                  <strong>{deleteModal.items[0].name}</strong>?
                </p>
                <div className="repo-modal-actions">
                  <button
                    className="repo-btn"
                    onClick={() => handleDelete("KEEP")}
                  >
                    Keep Files (Move to parent)
                  </button>
                  <button
                    className="repo-btn repo-btn-danger"
                    onClick={() => handleDelete("DELETE")}
                  >
                    Delete Everything
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>
                  Delete{" "}
                  {deleteModal.items.length > 1
                    ? `${deleteModal.items.length} Items`
                    : deleteModal.items[0]?.type === "folder"
                      ? "Folder"
                      : "File"}
                </h3>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {deleteModal.items.length > 1
                      ? `${deleteModal.items.length} selected items`
                      : deleteModal.items[0]?.name}
                  </strong>
                  ?
                </p>
                <div
                  className="repo-modal-actions"
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <button
                    className="repo-btn repo-btn-danger"
                    onClick={() => handleDelete("DELETE")}
                  >
                    Confirm Delete
                  </button>
                  <button
                    className="repo-btn"
                    onClick={() => setDeleteModal({ show: false, items: [] })}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Move Modal */}
      {moveModal.show && (
        <div
          className="repo-modal-overlay"
          onClick={() =>
            setMoveModal({ show: false, items: [], targetFolderId: null })
          }
        >
          <div
            className="repo-modal repo-animate-fade"
            style={{ maxWidth: "500px", width: "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="repo-modal-close"
              onClick={() =>
                setMoveModal({ show: false, items: [], targetFolderId: null })
              }
            >
              <X size={20} />
            </button>
            <h3>
              Move{" "}
              {moveModal.items.length === 1
                ? "Item"
                : `${moveModal.items.length} Items`}
            </h3>
            <p>
              Select destination folder for{" "}
              <strong>
                {moveModal.items.length === 1
                  ? moveModal.items[0].name
                  : "selected items"}
              </strong>
            </p>

            <div className="repo-folder-tree">
              <div
                className={`repo-folder-option ${moveModal.targetFolderId === null ? "selected" : ""}`}
                onClick={() =>
                  setMoveModal((prev) => ({ ...prev, targetFolderId: null }))
                }
              >
                <Folder size={16} /> Root Directory
              </div>

              {/* Recursive folder render */}
              {(() => {
                const renderFolders = (parentId = null, depth = 0) => {
                  const folders = items.filter(
                    (i) =>
                      i.type === "folder" &&
                      i.parentId === parentId &&
                      !moveModal.items.some((mi) => mi.id === i.id) &&
                      i.ownerType ===
                        (repoView === "global" ? "admin" : "user"),
                  );
                  return folders.map((f) => (
                    <React.Fragment key={f.id}>
                      <div
                        className={`repo-folder-option ${moveModal.targetFolderId === f.id ? "selected" : ""}`}
                        style={{ paddingLeft: `${(depth + 1) * 20 + 12}px` }}
                        onClick={() =>
                          setMoveModal((prev) => ({
                            ...prev,
                            targetFolderId: f.id,
                          }))
                        }
                      >
                        <Folder size={16} /> {f.name}
                      </div>
                      {renderFolders(f.id, depth + 1)}
                    </React.Fragment>
                  ));
                };
                return renderFolders(null, 0);
              })()}
            </div>

            <div
              className="repo-modal-actions"
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "row",
              }}
            >
              <button
                className="repo-btn repo-btn-primary"
                onClick={() => {
                  handleMove(moveModal.items, moveModal.targetFolderId);
                  setMoveModal({
                    show: false,
                    items: [],
                    targetFolderId: null,
                  });
                  setSelectedItems(new Set());
                }}
              >
                Save
              </button>
              <button
                className="repo-btn"
                onClick={() =>
                  setMoveModal({ show: false, items: [], targetFolderId: null })
                }
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {createFolderModal.show && (
        <div
          className="repo-modal-overlay"
          onClick={() => setCreateFolderModal({ show: false, name: "" })}
        >
          <div
            className="repo-modal repo-animate-fade"
            style={{ maxWidth: "400px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="repo-modal-close"
              onClick={() => setCreateFolderModal({ show: false, name: "" })}
            >
              <X size={20} />
            </button>
            <h3>New Folder</h3>
            <div className="repo-modal-body" style={{ marginTop: "1.5rem" }}>
              <input
                type="text"
                className="repo-search-input"
                style={{ width: "100%", padding: "0.8rem 1rem" }}
                placeholder="Folder Name"
                autoFocus
                value={createFolderModal.name}
                onChange={(e) =>
                  setCreateFolderModal((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              />
            </div>
            <div
              className="repo-modal-actions"
              style={{
                marginTop: "1.5rem",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="repo-btn repo-btn-primary d-flex align-items-center justify-content-center"
                onClick={handleCreateFolder}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </DndProvider>
  );
};

export default RepoOrganizer;
